import { useQuery } from '@tanstack/react-query';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { api } from '../lib/api';

export default function Reseller() {
  return (
    <div className="space-y-4">
      <div className="flex gap-3 border-b border-border">
        <NavLink to="tenants" className={({isActive}) => `px-3 py-2 ${isActive ? 'border-b-2 border-primary' : 'text-textSecondary'}`}>Tenants</NavLink>
        <NavLink to="billing" className={({isActive}) => `px-3 py-2 ${isActive ? 'border-b-2 border-primary' : 'text-textSecondary'}`}>Billing</NavLink>
        <NavLink to="health"  className={({isActive}) => `px-3 py-2 ${isActive ? 'border-b-2 border-primary' : 'text-textSecondary'}`}>Health</NavLink>
      </div>
      <Routes>
        <Route index element={<Navigate to="tenants" replace />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="billing" element={<Billing />} />
        <Route path="health"  element={<Health />} />
      </Routes>
    </div>
  );
}

function Tenants() {
  const { data: tenants = [] } = useQuery({ queryKey: ['reseller-tenants'], queryFn: () => api.reseller.tenants() });
  return (
    <div className="card p-0 overflow-hidden">
      <table className="table">
        <thead><tr><th>Tenant</th><th>Status</th><th>Extensions</th><th>Trunks</th><th>Calls (30d)</th><th>Minutes (30d)</th><th>Avg MOS</th></tr></thead>
        <tbody>
          {(tenants as any[]).map((t: any) => (
            <tr key={t.domain_uuid} className="hover:bg-elevated">
              <td>{t.domain_description || t.domain_name}</td>
              <td>{t.domain_enabled === 'true' ? <span className="badge-up">enabled</span> : <span className="badge-down">disabled</span>}</td>
              <td>{t.active_extensions}</td>
              <td>{t.trunks}</td>
              <td>{t.calls_30d}</td>
              <td>{t.minutes_30d}</td>
              <td className="font-mono">{t.avg_mos_30d || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Billing() {
  const month = new Date().toISOString().slice(0,7);
  const { data } = useQuery({ queryKey: ['reseller-billing', month], queryFn: () => api.reseller.billing(month) });
  const tenants = data?.tenants || [];
  const total = tenants.reduce((s: number, t: any) => s + (t.estimated_charge || 0), 0);
  return (
    <div className="space-y-4">
      <div className="card text-center">
        <div className="text-textSecondary text-sm">Estimated Total — {month}</div>
        <div className="text-4xl font-semibold mt-2">${total.toFixed(2)}</div>
      </div>
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Tenant</th><th>Extensions</th><th>Calls</th><th>Minutes</th><th>Estimated</th></tr></thead>
          <tbody>
            {tenants.map((t: any) => (
              <tr key={t.domain_uuid} className="hover:bg-elevated">
                <td>{t.domain_description || t.domain_name}</td>
                <td>{t.extensions}</td>
                <td>{t.calls}</td>
                <td>{t.minutes}</td>
                <td>${(t.estimated_charge || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Health() {
  const { data } = useQuery({ queryKey: ['reseller-health'], queryFn: () => api.reseller.health(), refetchInterval: 10_000 });
  return (
    <div className="grid grid-cols-3 gap-4">
      <Stat label="CPU" value={(data as any)?.cpu ? `${(data as any).cpu}%` : '—'} />
      <Stat label="RAM" value={(data as any)?.ram ? `${(data as any).ram}%` : '—'} />
      <Stat label="Disk" value={(data as any)?.disk || '—'} />
      <Stat label="Active Calls" value={(data as any)?.fs_active_calls || '—'} />
      <Stat label="Active Registrations" value={(data as any)?.fs_active_regs || '—'} />
      <Stat label="SBC nodes online" value={(data as any)?.sbc_nodes_online ?? '—'} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="card text-center">
      <div className="text-textSecondary text-sm">{label}</div>
      <div className="text-3xl font-semibold mt-2">{value}</div>
    </div>
  );
}
